import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircle2Icon, ClockIcon, MinusCircleIcon, CalendarIcon, DollarSignIcon, PlusIcon, ArrowLeftIcon } from 'lucide-react';
import { StateStatus, RenewalEntry, ChargeItem, LicenseData, User } from './types';
interface StateModalProps {
  user: User;
  stateName: string;
  isOpen: boolean;
  onClose: () => void;
  license: LicenseData;
  onUpdateLicense: (license: LicenseData) => void;
}
const STATUS_CONFIG: Record<StateStatus, {
  label: string;
  icon: typeof CheckCircle2Icon;
  activeClass: string;
}> = {
  not_started: {
    label: 'Not Started',
    icon: MinusCircleIcon,
    activeClass: 'bg-slate-700 text-slate-200'
  },
  in_progress: {
    label: 'In Progress',
    icon: ClockIcon,
    activeClass: 'bg-amber-500/20 text-amber-400'
  },
  complete: {
    label: 'Complete',
    icon: CheckCircle2Icon,
    activeClass: 'bg-emerald-500/20 text-emerald-400'
  }
};
const STATUS_ORDER: StateStatus[] = ['in_progress', 'complete'];
export function StateModal({
  user,
  stateName,
  isOpen,
  onClose,
  license,
  onUpdateLicense
}: StateModalProps) {
  const [isAddingRenewal, setIsAddingRenewal] = useState(false);
  const [newRenewalYear, setNewRenewalYear] = useState<number>(new Date().getFullYear() + 1);
  const [newRenewalFee, setNewRenewalFee] = useState<number>(0);
  const [newRenewalDate, setNewRenewalDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [newChargeName, setNewChargeName] = useState('');
  const [newChargeAmount, setNewChargeAmount] = useState<number>(0);
  const [isAddingCharge, setIsAddingCharge] = useState(false);
  const charges = license.charges || [];
  const chargesTotalCost = charges.reduce((sum, c) => sum + c.amount, 0);
  const handleStatusChange = (status: StateStatus) => {
    onUpdateLicense({
      ...license,
      status
    });
  };
  const handleAddCharge = () => {
    if (!newChargeName.trim()) return;
    const updatedCharges = [...charges, {
      name: newChargeName.trim(),
      amount: newChargeAmount
    }];
    const newCost = updatedCharges.reduce((sum, c) => sum + c.amount, 0);
    onUpdateLicense({
      ...license,
      charges: updatedCharges,
      cost: newCost
    });
    setNewChargeName('');
    setNewChargeAmount(0);
    setIsAddingCharge(false);
  };
  const handleRemoveCharge = (index: number) => {
    const updatedCharges = charges.filter((_, i) => i !== index);
    const newCost = updatedCharges.reduce((sum, c) => sum + c.amount, 0);
    onUpdateLicense({
      ...license,
      charges: updatedCharges,
      cost: newCost
    });
  };
  const handleStartDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      startDate: date
    });
  };
  const handleExpirationDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      expirationDate: date
    });
  };
  const handleSaveRenewal = () => {
    const newRenewals = [...license.renewals, {
      year: newRenewalYear,
      fee: newRenewalFee,
      date: newRenewalDate
    }].sort((a, b) => b.year - a.year);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
    setIsAddingRenewal(false);
    setNewRenewalYear(new Date().getFullYear() + 1);
    setNewRenewalFee(0);
    setNewRenewalDate(new Date().toISOString().split('T')[0]);
  };
  const handleRemoveRenewal = (index: number) => {
    const newRenewals = [...license.renewals];
    newRenewals.splice(index, 1);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-312" data-mp="3kkj2jyxbcuv8">
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-313" data-mp="3klu5xxmuk0lt">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-314" data-mp="35otb3l9czpj4" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-lg bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-315" data-mp="cucnoowihmpl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50" data-id="element-316" data-mp="3gbl5x9v5ioku">
            <div className="flex items-center gap-3" data-id="element-317" data-mp="1jfnlvwplzcup">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-318" data-mp="1ejostvtc56ny">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-319" data-mp="1r0racnmpg9fx" />
              </button>
              <div data-id="element-320" data-mp="1vrzfh7h058na">
                <h2 className="text-lg font-bold text-white" data-id="element-321" data-mp="1vrg09yiqkycy">{stateName}</h2>
                <p className="text-xs text-slate-500" data-id="element-322" data-mp="20r2m2wcmrmxd">
                  {user.name} · #{user.nmlsId}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-323" data-mp="134v6ysipac6r">
              <XIcon className="w-5 h-5" data-id="element-324" data-mp="1d0gipidkmqtp" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6" data-id="element-325" data-mp="1i1cgrfb8rir7">
            {/* Status */}
            <div data-id="element-326" data-mp="2og507u822xn4">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-327" data-mp="2oir8jw2rth6e">
                Status
              </label>
              <div className="flex bg-slate-800/80 rounded-xl p-1 gap-1" data-id="element-328" data-mp="tcp6g6rsl081">
                {STATUS_ORDER.map(s => {
                const isActive = license.status === s;
                const config = STATUS_CONFIG[s];
                const Icon = config.icon;
                return <button key={s} onClick={() => handleStatusChange(s)} className={`relative flex-1 flex items-center justify-center gap-2 py-2.5 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? config.activeClass : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/30'}`} data-id="element-329" data-mp="ycbkujjcwmif">
                      <Icon className="w-4 h-4 flex-shrink-0" data-id="element-330" data-mp="eoc66xlo8v7q" />
                      <span data-id="element-331" data-mp="3n4e2pd7f9s47">{config.label}</span>
                    </button>;
              })}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-332" data-mp="3j2k6bh49fa9u">
              <div data-id="element-333" data-mp="38xu8nnfw2sbf">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-334" data-mp="38xaos16ohtur">
                  Sponsorship Start Date
                </label>
                <div className="flex items-center bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/50 transition-all" data-id="element-335" data-mp="2z1qybuavvnde">
                  <CalendarIcon className="w-4 h-4 text-slate-500 mr-2" data-id="element-336" data-mp="3rpwjuj9rhgea" />
                  <input type="date" value={license.startDate || ''} onChange={e => handleStartDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                  colorScheme: 'dark'
                }} data-id="element-337" data-mp="jby9f2h70on" />
                </div>
              </div>

              <AnimatePresence data-id="element-338" data-mp="2bcet15iofzgd">
                {license.status === 'complete' && <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} data-id="element-339" data-mp="1sm5k1bqi0b6y">
                    <label className="block text-xs font-medium text-emerald-400/80 uppercase tracking-wider mb-2" data-id="element-340" data-mp="2baal2bfrti22">
                      Expiration Date
                    </label>
                    <div className="flex items-center bg-emerald-500/[0.05] border border-emerald-500/20 rounded-xl px-3 py-2.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/50 transition-all" data-id="element-341" data-mp="21jpm2mw6qbim">
                      <CalendarIcon className="w-4 h-4 text-emerald-500/70 mr-2" data-id="element-342" data-mp="2u3eh23ggo6zu" />
                      <input type="date" value={license.expirationDate || ''} onChange={e => handleExpirationDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                    colorScheme: 'dark'
                  }} data-id="element-343" data-mp="2p8qnhq6lomee" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Charges */}
            <div data-id="element-344" data-mp="14gcpsbdib0jd">
              <div className="flex items-center justify-between mb-3" data-id="element-345" data-mp="14hxj5xo34dfn">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-346" data-mp="18i8025df0o9a">
                  License Charges
                </label>
                {!isAddingCharge && <button onClick={() => setIsAddingCharge(true)} className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors bg-emerald-500/10 px-2.5 py-1.5 rounded-lg" data-id="element-347" data-mp="1igdzmgbr1iaz">
                    <PlusIcon className="w-3.5 h-3.5" data-id="element-348" data-mp="16tqapfkt4kzw" />
                    Add Charge
                  </button>}
              </div>

              {charges.length > 0 && <div className="space-y-1.5 mb-3" data-id="element-349" data-mp="1fqivkkxn8q4t">
                  {charges.map((charge, idx) => <div key={idx} className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/50 group" data-id="element-350" data-mp="1kplrnfvnbm0n" data-mpi={idx}>
                      <span className="text-sm text-slate-300" data-id="element-351" data-mp="1ynacinppqt5p">
                        {charge.name}
                      </span>
                      <div className="flex items-center gap-3" data-id="element-352" data-mp="8pfcvxuswhw">
                        <span className="text-sm text-slate-200 tabular-nums font-medium" data-id="element-353" data-mp="499e23y2s865">
                          ${charge.amount.toLocaleString()}
                        </span>
                        <button onClick={() => handleRemoveCharge(idx)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-0.5" aria-label="Remove charge" data-id="element-354" data-mp="3sx782fza8obq">
                          <XIcon className="w-3.5 h-3.5" data-id="element-355" data-mp="3f1wklnndb5he" />
                        </button>
                      </div>
                    </div>)}
                </div>}

              <AnimatePresence data-id="element-356" data-mp="anopjijmzgm5">
                {isAddingCharge && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden mb-3" data-id="element-357" data-mp="6mcwt45guf9q">
                    <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-4" data-id="element-358" data-mp="2svka7drbyg09">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-id="element-359" data-mp="2ewav6hev1rxv">
                        <div data-id="element-360" data-mp="2iwuwlx6gvhpt">
                          <label className="block text-xs text-slate-400 mb-1.5" data-id="element-361" data-mp="2ivthnhmbyb2t">
                            Charge Name
                          </label>
                          <input type="text" value={newChargeName} onChange={e => setNewChargeName(e.target.value)} placeholder="e.g. Application Fee" className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/50 placeholder:text-slate-600" data-id="element-362" data-mp="29wg8yr615qj5" />
                        </div>
                        <div data-id="element-363" data-mp="17jdfuudjp6g2">
                          <label className="block text-xs text-slate-400 mb-1.5" data-id="element-364" data-mp="17koq9d1hxcg5">
                            Amount
                          </label>
                          <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-emerald-500/50" data-id="element-365" data-mp="1glcvpjctq4hf">
                            <DollarSignIcon className="w-4 h-4 text-slate-500 mr-1.5" data-id="element-366" data-mp="1clcig21gnzby" />
                            <input type="number" min="0" value={newChargeAmount === 0 ? '' : newChargeAmount} placeholder="0" onChange={e => setNewChargeAmount(parseInt(e.target.value) || 0)} className="w-full bg-transparent text-sm text-slate-200 tabular-nums focus:outline-none" data-id="element-367" data-mp="1qhg59aa095ee" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-3" data-id="element-368" data-mp="cc4fc03tffbw">
                        <button onClick={() => {
                      setIsAddingCharge(false);
                      setNewChargeName('');
                      setNewChargeAmount(0);
                    }} className="text-sm text-slate-400 hover:text-slate-300 font-medium px-3 py-1.5" data-id="element-369" data-mp="gd6ekgxqfrdv">
                          Cancel
                        </button>
                        <button onClick={handleAddCharge} disabled={!newChargeName.trim()} className="text-sm bg-emerald-500 text-white px-4 py-1.5 rounded-lg hover:bg-emerald-600 transition-colors font-medium shadow-sm disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-370" data-mp="3tsul6x40loex">
                          Add
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Total */}
              <div className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2.5 border border-slate-700/50" data-id="element-371" data-mp="15dm1chml52l8">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-372" data-mp="vfogm43xpnxd">
                  Total License Cost
                </span>
                <span className="text-sm font-bold text-white tabular-nums" data-id="element-373" data-mp="qh3m8lbd5ygu">
                  ${chargesTotalCost.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Renewals */}
            <AnimatePresence data-id="element-374" data-mp="2ksicd38adzx4">
              {license.status === 'complete' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="pt-4 border-t border-slate-800" data-id="element-375" data-mp="2gtsscrsqtk4i">
                  <div className="flex items-center justify-between mb-4" data-id="element-376" data-mp="wwby8j5aj06j">
                    <label className="block text-xs font-medium text-blue-400/80 uppercase tracking-wider" data-id="element-377" data-mp="wv0rcagjtcb7">
                      Renewals
                    </label>
                    {!isAddingRenewal && <button onClick={() => setIsAddingRenewal(true)} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors bg-blue-500/10 px-2.5 py-1.5 rounded-lg" data-id="element-378" data-mp="sv0543n7jwtl">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-379" data-mp="3dj4p0pxopxnw" />
                        Add Renewal
                      </button>}
                  </div>

                  {license.renewals.length > 0 && <div className="space-y-2 mb-4" data-id="element-380" data-mp="3f36bqkz39x87">
                      {license.renewals.map((renewal, idx) => <div key={idx} className="flex items-center justify-between bg-slate-800/40 rounded-xl px-4 py-3 border border-slate-700/50 group" data-id="element-381" data-mp="3a4laqjc0j9ip" data-mpi={idx}>
                          <div className="flex flex-col" data-id="element-382" data-mp="2hgpflj23dpo1">
                            <span className="text-sm text-slate-200 font-medium" data-id="element-383" data-mp="2w422bb3my1g0">
                              {renewal.year} Renewal
                            </span>
                            <span className="text-xs text-slate-500" data-id="element-384" data-mp="23nhn7p66sq5a">
                              Submitted: {renewal.date}
                            </span>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-385" data-mp="1d538mqu1z0gg">
                            <span className="text-sm text-slate-300 tabular-nums font-medium" data-id="element-386" data-mp="1d6wh8bo87ur3">
                              ${renewal.fee}
                            </span>
                            <button onClick={() => handleRemoveRenewal(idx)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1" aria-label="Remove renewal" data-id="element-387" data-mp="1n27v6rrt8xxs">
                              <XIcon className="w-4 h-4" data-id="element-388" data-mp="2117my8amdsy6" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <AnimatePresence data-id="element-389" data-mp="2auofl8z0t209">
                    {isAddingRenewal && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-390" data-mp="2ftrj2ozcr064">
                        <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-4" data-id="element-391" data-mp="ru5ytrknu1q9">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-id="element-392" data-mp="mv2te60rpthr">
                            <div data-id="element-393" data-mp="mxf9seqvnvsp">
                              <label className="block text-xs text-slate-400 mb-1.5" data-id="element-394" data-mp="i3t97kxznid9">
                                Year
                              </label>
                              <input type="number" value={newRenewalYear} onChange={e => setNewRenewalYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" data-id="element-395" data-mp="d3f8knypvap9" />
                            </div>
                            <div data-id="element-396" data-mp="266viem69mq1r">
                              <label className="block text-xs text-slate-400 mb-1.5" data-id="element-397" data-mp="218kcgmj3czot">
                                Fee
                              </label>
                              <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-blue-500/50" data-id="element-398" data-mp="1wg0vrgp3xofs">
                                <DollarSignIcon className="w-4 h-4 text-slate-500 mr-1.5" data-id="element-399" data-mp="2oyel0rwdo239" />
                                <input type="number" min="0" value={newRenewalFee === 0 ? '' : newRenewalFee} placeholder="0" onChange={e => setNewRenewalFee(parseInt(e.target.value) || 0)} className="w-full bg-transparent text-sm text-slate-200 tabular-nums focus:outline-none" data-id="element-400" data-mp="2k6whpx6vtpf4" />
                              </div>
                            </div>
                            <div data-id="element-401" data-mp="13fatncqkoagw">
                              <label className="block text-xs text-slate-400 mb-1.5" data-id="element-402" data-mp="13ej70reydn2r">
                                Date Submitted
                              </label>
                              <input type="date" value={newRenewalDate} onChange={e => setNewRenewalDate(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" style={{
                          colorScheme: 'dark'
                        }} data-id="element-403" data-mp="1dea5t5ajqo8n" />
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-3" data-id="element-404" data-mp="5zwqkgtme3ju">
                            <button onClick={() => setIsAddingRenewal(false)} className="text-sm text-slate-400 hover:text-slate-300 font-medium px-3 py-1.5" data-id="element-405" data-mp="10tkqu5tftat">
                              Cancel
                            </button>
                            <button onClick={handleSaveRenewal} className="text-sm bg-blue-500 text-white px-4 py-1.5 rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm" data-id="element-406" data-mp="i9mejxhontwu">
                              Save Renewal
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end" data-id="element-407" data-mp="3ls5v42r01bbt">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-408" data-mp="3qq7phjtvkx2l">
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}